import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop'
import { ComponentType } from '@angular/cdk/portal'
import { Component, Injector, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { Store } from '@ngrx/store'
import { AbstractComponent, Card, CardService, CompanyCrmLists, CompanyService, FilterDefinition, Funnel, HEALTH_BAND_LABEL, HEALTH_BANDS, QUALIFICATION_LABEL, Stage, StageService, StageTotals, URGENCY_LABEL } from 'lib-trend-core'
import { debounceTime, filter, finalize, Observable, Subject, take, takeUntil } from 'rxjs'
import { CardDetailsComponent } from './card-details/card-details.component'
import { FilterPanelComponent } from './filter-panel/filter-panel.component'
import { FunnelFormComponent } from './funnel-form/funnel-form.component'
import { getFunnel, getFunnels, getStages, setFunnel, setStages } from './state/actions'
import { AppCRMState, ParamsState } from './state/app.state'
import { CRMEffects } from './state/effects'
import { funnelSelector, funnelsSelector, queryParamsSelector, stagesSelector } from './state/selectors'
import { CardUploadComponent } from './card-upload/card-upload.component'

@Component({
    selector: 'app-kanban',
    templateUrl: './kanban.component.html',
    styleUrls: ['./kanban.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class KanbanComponent extends AbstractComponent {
  readonly funnels$: Observable<Array<Funnel>> = this.store.select(funnelsSelector);
  readonly funnel$: Observable<Funnel> = this.store.select(funnelSelector);
  readonly stages$: Observable<Array<Stage>> = this.store.select(stagesSelector);
  readonly queryParams$: Observable<ParamsState> = this.store.select(queryParamsSelector);

  /** The one writer of the board's filter state; the overview's presets go through it (SPEC-2026-10 RF-08). */
  @ViewChild(FilterPanelComponent) private filterPanel?: FilterPanelComponent;

  /** The board's active filter set, mirrored from the store (RF-03). */
  private boardParams: Record<string, unknown> = {};

  /**
   * The board's free-text search, mirrored from the store.
   *
   * Kept apart from `boardParams` because the store keeps it apart: `ParamsState` has
   * a `search` slot of its own, and the columns pass it to the pager as its own query
   * param. The header used to send only `params`, so a searched board showed the
   * UNSEARCHED total.
   */
  private boardSearch: string = '';

  /**
   * Coalesces the header reload (RF-03).
   *
   * Three signals move the figures: the selected pipeline, the active filters, and
   * the stage list the board republishes after every card move, create or delete.
   * The third one did not reload the header at all, so a moved card left both
   * columns on their pre-move value. `debounceTime(0)` folds signals that land in
   * the same tick into one request — it does NOT deduplicate signals separated by a
   * round trip, and is not meant to.
   */
  private readonly stageTotalsTrigger = new Subject<void>();

  /** Reloads the base totals with the pipeline and the stage list — never with the filters, which cannot change them. */
  private readonly baseTotalsTrigger = new Subject<void>();

  /** Per-stage count and value of the funnel with NO filter: the "de N" each column shows (filter bar, 2026-10-27). */
  private baseFigures = new Map<string, StageTotals>();

  /** Deals under the current filters and in the whole funnel — the bar's "N de M". Null until the totals arrive. */
  shownCount: number | null = null;
  totalCount: number | null = null;

  /**
   * Loads the per-stage count and value each column header shows (RF-03).
   *
   * Driven by the board's ACTIVE query, taken from the same store selector the
   * columns read. The first version of this called the pipeline summary with a
   * hardcoded current-month window and no filters, so a stage reading `(0)` could
   * report a non-zero value beside it — two numbers, two populations, one header.
   *
   * Not summed on the client: a column paginates, so adding up the cards it
   * happens to have loaded understates every stage past the first page. Since
   * SPEC-2026-10 (RF-16) the COUNT comes from here too: a copy is listed in its
   * column but counted in neither header number, which the pager's total cannot say.
   */
  private loadStageTotals(params: Record<string, unknown>): void {
    if (!this.selectedFunnel?._id || !this.hasDealRadar) return;

    this.cardService
      .getStageTotals({
        ...(params as Record<string, string>),
        funnel: this.selectedFunnel._id,
        // The store keeps the free-text search outside `params` (ParamsState), and
        // the columns pass it to the pager as its own query param. Sending only
        // `params` made the header describe the UNSEARCHED funnel.
        ...(this.boardSearch ? { search: this.boardSearch } : {}),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.stageFigures = new Map((rows ?? []).map(row => [row.stage, row]));
          this.shownCount = this.sumCounts(this.stageFigures);
        },
        // A failed load leaves the header on the pager's count alone, never a wrong sum.
        error: () => {
          this.stageFigures = new Map();
          this.shownCount = null;
        },
      });
  }

  /**
   * The funnel's per-stage figures with NO filter (filter bar, 2026-10-27): the "de N"
   * beside each column's count and the "M" of the bar's "N de M" — what the filters took
   * away, read from the same endpoint as the header figures so the two populations differ
   * by the filters alone.
   */
  private loadBaseTotals(): void {
    if (!this.selectedFunnel?._id || !this.hasDealRadar) return;

    this.cardService
      .getStageTotals({ funnel: this.selectedFunnel._id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.baseFigures = new Map((rows ?? []).map(row => [row.stage, row]));
          this.totalCount = this.sumCounts(this.baseFigures);
        },
        // Without the base, the columns show their count alone and the bar hides its "N de M".
        error: () => {
          this.baseFigures = new Map();
          this.totalCount = null;
        },
      });
  }

  private sumCounts(figures: Map<string, StageTotals>): number {
    let total = 0;
    figures.forEach(row => total += row.count ?? 0);
    return total;
  }

  /** Null until the base totals arrive; the column then shows its count alone. */
  stageBaseCount(idStage: string): number | null {
    return this.baseFigures.get(idStage)?.count ?? null;
  }

  /** Null until the totals arrive, so the column header shows the count alone. */
  stageValue(idStage: string): number | null {
    return this.stageFigures.get(idStage)?.value ?? null;
  }

  /** Null until the totals arrive; the column then falls back to its pager's total. */
  stageCount(idStage: string): number | null {
    return this.stageFigures.get(idStage)?.count ?? null;
  }

  /** A preset picked in the pipeline overview is the board's period (RF-08). */
  onOverviewPeriod(range: { from: Date; to: Date }): void {
    this.filterPanel?.setDateRange(range.from, range.to);
  }

  toggleOverview(): void {
    this.showOverview = !this.showOverview;
  }

  filterDefs: FilterDefinition[] = [
    /**
     * The board's period — "Data de criação" since the filter bar (2026-10-27), a filter
     * like any other with NO default: the bar opens on the whole funnel, and the user picks
     * a range when one is wanted. It still travels as `dateStart` / `dateEnd` (the
     * `daterange` convention keys `<key>Start` / `<key>End`), which the API reads as the
     * board's period (RF-03): won by winning moment, lost by losing moment, open by creation.
     */
    { key: 'date', label: 'kanban.filters.createdAt', type: 'daterange', defaultValue: null },
    { key: 'product', label: 'kanban.filters.product', type: 'multiselect', defaultValue: [], options: [] },
    { key: 'customer', label: 'kanban.filters.customer', type: 'multiselect', defaultValue: [], options: [] },
    { key: 'user', label: 'kanban.filters.user', type: 'multiselect', defaultValue: [], options: [] },
    { key: 'priority', label: 'kanban.filters.priority', type: 'multiselect', defaultValue: [], options: [] },
    { key: 'lossReason', label: 'kanban.filters.lossReason', type: 'multiselect', defaultValue: [], options: [] },
    { key: 'winDate', label: 'kanban.filters.winDate', type: 'daterange', defaultValue: null },
    { key: 'lostDate', label: 'kanban.filters.lostDate', type: 'daterange', defaultValue: null },
    /**
     * The out-of-profile toggle (RF-18) lives on the count button of the toolbar, so it is
     * never offered in the "add filter" menu — but while it is on, the bar shows it as a
     * chip like any other filter, removable from there too.
     */
    { key: 'onlyOutOfIcp', label: 'kanban.filters.outOfIcp', type: 'select', defaultValue: null, options: [{ value: 'true', label: 'kanban.filters.outOfIcpOnly' }], hidden: true },

    /**
     * Daily-radar filters (RF-16, RF-17, RF-18).
     *
     * Appended in `ngOnInit` for companies with the CRM module — see
     * `radarFilterDefs`. The radar has no flag of its own: it is part of the CRM, so
     * any company with `moduleCRM` gets it.
     */
  ];

  /** Available to any company with the CRM module. */
  private readonly radarFilterDefs: FilterDefinition[] = [
    {
      key: 'urgency', label: 'kanban.filters.urgency', type: 'multiselect', defaultValue: [],
      // Derived from the map the badge renders, so a filter can never be labelled
      // differently from the state it filters for. Safe to derive because the map is
      // keyed by the closed `CardUrgency` union.
      options: Object.entries(URGENCY_LABEL).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'qualification', label: 'kanban.filters.qualification', type: 'multiselect', defaultValue: [],
      options: Object.entries(QUALIFICATION_LABEL).map(([value, label]) => ({ value, label })),
    },
    {
      // The specific origin (a campaign, a lead source). Options are loaded from
      // the pipeline's own deals by the filter panel — a static list would offer
      // origins this company never used.
      key: 'origin', label: 'kanban.filters.origin', type: 'multiselect', defaultValue: [], options: [],
    },
    /**
     * There is no "origin type" filter (owner decision 2026-08-20).
     *
     * The specific `origin` filter above already answers the question this one asked,
     * and it answers it better: its options are derived from the deals actually in the
     * pipeline, so it never offers a bucket this company never used. The API still
     * accepts the `originType` param — `CardService.buildFilterQuery` reads it — so the
     * filter can come back without touching the backend.
     */
    { key: 'scoreMin', label: 'kanban.filters.scoreMin', type: 'number', defaultValue: null },
    { key: 'scoreMax', label: 'kanban.filters.scoreMax', type: 'number', defaultValue: null },
    {
      key: 'stagnant', label: 'kanban.filters.stagnant', type: 'select', defaultValue: null,
      options: [
        { value: 'true', label: 'kanban.filters.stagnantOnly' },
        { value: 'false', label: 'kanban.filters.stagnantExclude' },
      ],
    },
    // E3 RF-34. The qualification filter above now matches the DECIDED level, or the
    // recommendation while nobody decided — the same value the badge shows.
    {
      key: 'readyForPromotion', label: 'kanban.filters.readyForPromotion', type: 'select', defaultValue: null,
      options: [{ value: 'true', label: 'kanban.filters.readyForPromotionOnly' }],
    },
    {
      key: 'healthBand', label: 'kanban.filters.healthBand', type: 'multiselect', defaultValue: [],
      // The six current bands only — the legacy STABLE is never offered (ADR-003).
      options: HEALTH_BANDS.map((value) => ({ value, label: HEALTH_BAND_LABEL[value] })),
    },
    {
      key: 'exhausted', label: 'kanban.filters.exhausted', type: 'select', defaultValue: null,
      options: [{ value: 'true', label: 'kanban.filters.exhaustedOnly' }],
    },
    /**
     * There is no "Lista negra" and no "Perfil ideal" filter (owner decision
     * 2026-08-21), for the same reason the "origin type" filter above is absent: both
     * were single-option opt-ins that spent a row of the panel each on a question
     * almost nobody asks of the board.
     *
     * Consequences, deliberately accepted:
     * - Blacklisted leads are excluded by default (RF-17) and there is no longer a way
     *   to opt them back in HERE. The marking itself stays visible and reversible on
     *   the Customers screen, which is where it is applied.
     * - Restricting the board to out-of-profile deals stays reachable through the
     *   out-of-profile COUNT button in the filter panel, which toggles the same
     *   `onlyOutOfIcp` param (RF-18) — the count is the reason that button exists.
     *
     * The API still reads both params in `CardService.buildFilterQuery`, so either
     * filter can come back without touching the backend.
     */
  ];


  viewMode: 'board' | 'list' = 'board';

  /**
   * The funnel overview is collapsed by default: it answers a supervisor's weekly
   * question (US-05), not the salesperson's daily one, and expanding it by default
   * would push the board down for everyone (spec §12 guard metric).
   */
  showOverview = false;

  /**
   * The daily radar is part of the CRM, not a separately licensed feature: a company
   * that has `moduleCRM` gets the figures, the overview, the enriched card and the new
   * filters. There is no radar-specific flag.
   */
  get hasDealRadar(): boolean {
    return this.getModuleCRM();
  }

  /** Per-stage count and value from `stage-totals`, keyed by stage id (RF-03). */
  private stageFigures = new Map<string, StageTotals>();

  stages: Stage[] = [];
  funnels: Funnel[] = [];
  selectedFunnel: Funnel | null = null;
  connectedDropLists: string[] = [];

  private selectedFunnelStorageKey: string;
  private hasInitializedFunnel = false;

  constructor(
    injector: Injector,
    private store: Store<AppCRMState>,
    public dialog: MatDialog,
    private stageService: StageService,
    private cardService: CardService,
    private companyService: CompanyService,
    private crmEffects: CRMEffects,
  ) {
    super(injector)
    this.selectedFunnelStorageKey = this.constants?.['kanbanSelectedFunnelId'] ?? 'kanbanSelectedFunnelId';
  }

  /**
   * Account filters (E2 RF-09): segment and company size, with the options the company's
   * lists actually hold. Loaded here because the options are DATA — the panel translates
   * fixed-option labels, and a value absent from the catalogs comes back unchanged.
   */
  private accountFilterDefs(lists: CompanyCrmLists): FilterDefinition[] {
    const asOptions = (values: Array<string>) => (values ?? []).map(value => ({ value, label: value }));
    return [
      { key: 'segment', label: 'kanban.filters.segment', type: 'multiselect', defaultValue: [], options: asOptions(lists?.segments), group: 'kanban.filterPanel.groupRadar' },
      { key: 'employeeBand', label: 'kanban.filters.employeeBand', type: 'multiselect', defaultValue: [], options: asOptions(lists?.employeeBands), group: 'kanban.filterPanel.groupRadar' },
    ];
  }

  ngOnInit() {
    /**
     * The radar filters follow the same flag as the rest of the feature (plan §9).
     *
     * Sectioned here, at the one place both batches meet: the panel renders a flat list
     * otherwise, and fifteen unlabelled rows is a list nobody reads. The section is a
     * property of where a filter comes from, so it is stamped on the way in rather than
     * repeated on sixteen literals.
     */
    this.filterDefs = [
      ...this.filterDefs.map(def => ({ ...def, group: 'kanban.filterPanel.groupDeal' })),
      ...(this.hasDealRadar
        ? this.radarFilterDefs.map(def => ({ ...def, group: 'kanban.filterPanel.groupRadar' }))
        : []),
    ];

    if (this.hasDealRadar) {
      this.companyService.getCrmLists()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (lists) => this.filterDefs = [...this.filterDefs, ...this.accountFilterDefs(lists)],
          // Without the lists the board simply offers no account filters.
          error: () => undefined,
        });
    }

    this.stageTotalsTrigger
      .pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe(() => this.loadStageTotals(this.boardParams));

    this.baseTotalsTrigger
      .pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe(() => this.loadBaseTotals());

    this.store.dispatch(setFunnel({ funnel: null }));
    this.store.dispatch(setStages({ stages: [] }));

    const storedFunnelId = this.getStoredFunnelId();
    this.store.dispatch(getFunnels({ search: this.searchString, params: this.searchParams }));

    this.funnels$.pipe(
      takeUntil(this.destroy$),
      filter(funnels => funnels.length > 0)
    ).subscribe(funnels => {
      this.funnels = funnels;

      if (this.hasInitializedFunnel) return;

      const validFunnel = storedFunnelId
        ? funnels.find(f => f._id === storedFunnelId)
        : funnels[0];

      if (validFunnel?._id) {
        this.hasInitializedFunnel = true;
        this.store.dispatch(getFunnel({ idFunnel: validFunnel._id }));
        this.funnel$.pipe(
          filter(f => f?._id === validFunnel._id),
          take(1)
        ).subscribe(() => {
          this.store.dispatch(getStages({ idFunnel: validFunnel._id }));
        });
      }
    });

    this.funnel$.pipe(takeUntil(this.destroy$)).subscribe(funnel => {
      this.selectedFunnel = funnel;
      // Per-stage totals follow the selected pipeline (RF-03), and so does the base they are read against.
      this.stageTotalsTrigger.next();
      this.baseTotalsTrigger.next();
    });

    // …and the active filters. Same selector the columns read, so the header and
    // the cards under it always describe the same population.
    this.queryParams$.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.boardParams = params?.params ?? {};
      this.boardSearch = params?.search ?? '';
      this.stageTotalsTrigger.next();
    });
    this.stages$.pipe(takeUntil(this.destroy$)).subscribe(stages => {
      this.stages = stages;
      this.connectedDropLists = stages.map(s => s._id);
      /**
       * The stage list is the board's reload signal: `getStage` maps to `setStages`
       * with a fresh array (state/effects.ts:117), which is what makes every column
       * refetch its cards after a card is moved. The header has to follow the same
       * signal or it keeps the pre-move value.
       */
      this.stageTotalsTrigger.next();
      this.baseTotalsTrigger.next();
    });

    this.openCardFromQueryParam();
  }

  /**
   * Deep link `?card=<id>` (chat work references, DEV-1984): fetches the card
   * and opens the SAME details dialog the board columns open — the old
   * `/kanban/card/:id` route mounted the dialog component as a page, which
   * rendered an empty shell (MAT_DIALOG_DATA never provided) and was removed.
   * The param is dropped up front (replaceUrl) so history/refresh never
   * reopen the dialog, even when the load fails.
   */
  private openCardFromQueryParam(): void {
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const cardId = params.get('card');
        if (!cardId) return;

        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { card: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });

        this.cardService.getById(cardId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (card: Card) => {
              if (card?._id) {
                this.dialog.open(CardDetailsComponent, { width: '1000px', data: card });
              } else {
                this.alertService.error(this.t('kanban.cardDeepLink.notFound'));
              }
            },
            error: () => this.alertService.error(this.t('kanban.cardDeepLink.notFound')),
          });
      });
  }

  override ngOnDestroy(): void {
    this.hasInitializedFunnel = false;
    super.ngOnDestroy();
    this.crmEffects.destroyEffects();
  }

  addStage(): void {
    if (!this.selectedFunnel?._id) return;
    const payload = {
      name: 'Nova etapa',
      order: this.stages.length + 1,
      description: '',
      funnel: <Funnel>{ _id: this.selectedFunnel._id },
      company: this.getCompanyCurrentUser(),
      bgColor: this.generateStageColor(),
      removed: false,
    };

    this.loading = true;

    this.stageService.create(payload as Stage).pipe(takeUntil(this.destroy$), finalize(() => { this.loading = false; }))
      .subscribe({
        next: (createdStage) => {
          this.stages = [...this.stages, createdStage];
          this.store.dispatch(setStages({ stages: this.stages }));
        },
        error: (err) => {
          this.alertService.error(this.t('kanban.kanban.stageCreateError'));
        }
      });
  }

  onStageDrop(event: CdkDragDrop<Stage[]>) {
    if (!event || event.previousIndex === event.currentIndex || !Array.isArray(event.container?.data)) return;
    const funnelId = this.selectedFunnel?._id;
    if (!funnelId) return;

    const previousSnapshot = [...this.stages];
    const stageList = [...event.container.data];

    moveItemInArray(stageList, event.previousIndex, event.currentIndex);
    this.store.dispatch(setStages({ stages: stageList }));

    this.stageService.reorderStages(funnelId, stageList.map(s => s._id))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.alertService.success(this.t('kanban.kanban.stagesReordered'));
        },
        error: () => this.store.dispatch(setStages({ stages: previousSnapshot }))
      });
  }

  private openDialog<T>(component: ComponentType<T>, data?: unknown, width = '815px', onClose?: (result: unknown) => void): MatDialogRef<T> {
    const ref = this.dialog.open(component, { width, data });
    if (onClose) ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(onClose);
    return ref;
  }

  openFunnelForm(funnel: Funnel | null = null) {
    const isCreating = !funnel?._id;

    this.openDialog(FunnelFormComponent, funnel, '815px', (result: any) => {
      if (!result) return;

      if (result.action === 'delete') {
        const newFunnel = this.funnels.find(f => f._id !== funnel?._id);
        if (newFunnel) {
          this.selectFunnel(newFunnel);
        } else {
          this.store.dispatch(setFunnel({ funnel: null }));
          this.setStoredFunnelId(null);
        }
        this.store.dispatch(getFunnels({ search: this.searchString, params: this.searchParams }));
        return;
      }

      if (result?._id) {
        this.store.dispatch(setFunnel({ funnel: result }));

        if (isCreating) {
          this.setStoredFunnelId(result._id);
          this.store.dispatch(getFunnels({ search: this.searchString, params: this.searchParams }));
          this.store.dispatch(getStages({ idFunnel: result._id }));
        }
      }
    });
  }

  selectFunnel(funnel: Funnel) {
    if (!funnel?._id) return;
    if (this.selectedFunnel?._id === funnel._id) return;

    this.store.dispatch(setFunnel({ funnel }));
    this.store.dispatch(getStages({ idFunnel: funnel._id }));
    this.searchParams = { funnel: funnel._id };
    this.setStoredFunnelId(funnel._id);
  }

  private handleFunnelStorage(action: 'get' | 'set' | 'remove', value?: string): string | null {
    try {
      if (action === 'get') return localStorage.getItem(this.selectedFunnelStorageKey);
      if (action === 'remove' || !value) localStorage.removeItem(this.selectedFunnelStorageKey);
      else localStorage.setItem(this.selectedFunnelStorageKey, value);
      return null;
    } catch (err) {
      console.error(`Storage operation failed: ${action}`, err);
      return null;
    }
  }

  private getStoredFunnelId() { return this.handleFunnelStorage('get'); }
  private setStoredFunnelId(id: string | null) { return this.handleFunnelStorage(id ? 'set' : 'remove', id || undefined); }

  private generateStageColor(): string {
    const isRedOrGreen = (hex: string) => {
      const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.substring(i, i + 2), 16));
      return (r > g && r > b) || (g > r && g > b);
    }
    let color: string;
    do {
      color = '#' + Array.from({ length: 6 }, () => '0123456789ABCDEF'[Math.floor(Math.random() * 16)]).join('');
    } while (isRedOrGreen(color))
    return color;
  }

  import(): void {
    const dialogRef = this.dialog.open(CardUploadComponent, {
      width: '58vw',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) { }
    });
  }
}
